// components/admin/Dashboard.js - Fully Responsive Mobile-First Design
import React, { useState, useMemo, useEffect } from 'react';
import { ShoppingBag, DollarSign, Package, Truck, TrendingUp, TrendingDown, Calendar, RefreshCcw, ChevronLeft, ChevronRight } from 'lucide-react';

const Dashboard = ({ orders, loading }) => {
  const [period, setPeriod] = useState('today');
  const [customDate, setCustomDate] = useState(new Date().toISOString().split('T')[0]);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showAllStatus, setShowAllStatus] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const analytics = useMemo(() => {
    if (!orders) return null;
    const today = new Date(); 
    today.setHours(0,0,0,0);
    const startOfWeek = new Date(today); 
    startOfWeek.setDate(today.getDate() - today.getDay());
    const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    
    let startDate = today;
    if (period === 'week') startDate = startOfWeek;
    else if (period === 'month') startDate = startOfMonth;
    else if (period === 'custom') startDate = new Date(customDate); 
    startDate.setHours(0,0,0,0);
    
    const filteredOrders = orders.filter(o => new Date(o.orderDate) >= startDate);
    const deliveredOrders = filteredOrders.filter(o => o.orderStatus === 'delivered');
    const revenue = deliveredOrders.reduce((s,o) => s + (o.totalAmount||0), 0);
    const totalOrders = filteredOrders.length;
    const pending = orders.filter(o => o.orderStatus === 'out').length;
    
    // Daily trend for last 7 days
    const dailyData = [];
    for (let i=6; i>=0; i--) {
      const d = new Date(today); 
      d.setDate(today.getDate() - i); 
      d.setHours(0,0,0,0);
      const next = new Date(d); 
      next.setDate(d.getDate()+1);
      const dayOrders = orders.filter(o => { 
        const od = new Date(o.orderDate); 
        return od >= d && od < next; 
      });
      dailyData.push({ 
        date: d.toLocaleDateString('en-IN', { weekday: isMobile ? 'short' : 'short', day: 'numeric' }), 
        orders: dayOrders.length, 
        revenue: dayOrders.filter(o=>o.orderStatus==='delivered').reduce((s,o)=>s+(o.totalAmount||0),0) 
      });
    }
    
    const statusCounts = {
      pending: orders.filter(o=>o.orderStatus==='pending').length,
      confirmed: orders.filter(o=>o.orderStatus==='confirmed').length,
      processing: orders.filter(o=>o.orderStatus==='processing').length,
      out: pending,
      delivered: orders.filter(o=>o.orderStatus==='delivered').length,
      cancelled: orders.filter(o=>o.orderStatus==='cancelled').length
    };
    const totalRevenue = orders.filter(o=>o.orderStatus==='delivered').reduce((s,o)=>s+(o.totalAmount||0),0);
    
    // Compare with previous period
    let prevStartDate;
    if (period === 'today') { 
      prevStartDate = new Date(today); 
      prevStartDate.setDate(today.getDate()-1); 
    } else if (period === 'week') { 
      prevStartDate = new Date(startOfWeek); 
      prevStartDate.setDate(startOfWeek.getDate()-7); 
    } else if (period === 'month') { 
      prevStartDate = new Date(startOfMonth); 
      prevStartDate.setMonth(startOfMonth.getMonth()-1); 
    } else prevStartDate = null;
    
    let prevRevenue = 0, prevOrders = 0;
    if (prevStartDate) {
      const prevEnd = new Date(prevStartDate); 
      prevEnd.setDate(prevStartDate.getDate() + (period==='week'?7:period==='month'?30:1));
      const prevFiltered = orders.filter(o => { 
        const od = new Date(o.orderDate); 
        return od >= prevStartDate && od < prevEnd; 
      });
      prevOrders = prevFiltered.length;
      prevRevenue = prevFiltered.filter(o=>o.orderStatus==='delivered').reduce((s,o)=>s+(o.totalAmount||0),0);
    }
    const ordersGrowth = prevOrders ? ((totalOrders - prevOrders)/prevOrders)*100 : 0;
    const revenueGrowth = prevRevenue ? ((revenue - prevRevenue)/prevRevenue)*100 : 0;
    
    return { 
      periodOrders: totalOrders, 
      periodRevenue: revenue, 
      totalRevenue, 
      pending, 
      statusCounts, 
      dailyData, 
      ordersGrowth, 
      revenueGrowth, 
      totalOrdersCount: orders.length 
    };
  }, [orders, period, customDate, isMobile]);

  if (loading || !analytics) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: isMobile ? 40 : 60, flexDirection: 'column', gap: 16 }}>
        <RefreshCcw size={isMobile ? 32 : 40} className="spinner" color="#2874f0" />
        <p style={{ color: '#64748b', fontSize: isMobile ? '0.85rem' : '0.9rem' }}>Loading dashboard data...</p>
      </div>
    );
  }

  const formatCurrency = (amt) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amt);
  const maxRevenue = Math.max(...analytics.dailyData.map(d=>d.revenue), 1);

  const statusColors = { 
    pending: '#f59e0b', 
    confirmed: '#3b82f6', 
    processing: '#8b5cf6', 
    out: '#8b5cf6', 
    delivered: '#10b981', 
    cancelled: '#ef4444' 
  };

  const statusLabels = {
    pending: 'Pending',
    confirmed: 'Confirmed',
    processing: 'Processing',
    out: 'Out for Delivery',
    delivered: 'Delivered',
    cancelled: 'Cancelled'
  };

  // Get top 3 statuses for mobile view
  const getTopStatuses = () => {
    const entries = Object.entries(analytics.statusCounts);
    return entries.sort((a, b) => b[1] - a[1]).slice(0, 3);
  };

  return (
    <div style={{ paddingBottom: isMobile ? 20 : 0 }}>
      {/* Period Selector - Mobile Optimized */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: 20, 
        flexWrap: 'wrap', 
        gap: 12,
        flexDirection: isMobile ? 'column' : 'row'
      }}>
        <div style={{ 
          display: 'flex', 
          gap: 6, 
          background: 'white', 
          padding: 4, 
          borderRadius: 10, 
          border: '1px solid #e2e8f0',
          width: isMobile ? '100%' : 'auto',
          justifyContent: 'center'
        }}>
          {['today','week','month','custom'].map(p => (
            <button 
              key={p} 
              onClick={() => setPeriod(p)} 
              style={{ 
                padding: isMobile ? '6px 12px' : '6px 16px', 
                borderRadius: 8, 
                border: 'none', 
                background: period === p ? '#2874f0' : 'transparent', 
                color: period === p ? 'white' : '#475569', 
                fontSize: isMobile ? '0.7rem' : '0.8rem', 
                cursor: 'pointer',
                fontWeight: period === p ? 600 : 400,
                flex: isMobile ? 1 : 'auto',
                whiteSpace: 'nowrap'
              }}
            >
              {p === 'today' ? 'Today' : p === 'week' ? 'Week' : p === 'month' ? 'Month' : 'Custom'}
            </button>
          ))}
        </div>
        {period === 'custom' && (
          <input 
            type="date" 
            value={customDate} 
            onChange={(e) => setCustomDate(e.target.value)} 
            style={{ 
              padding: isMobile ? '8px 12px' : '6px 10px', 
              border: '1px solid #e2e8f0', 
              borderRadius: 8, 
              fontSize: isMobile ? '0.75rem' : '0.8rem',
              width: isMobile ? '100%' : 'auto'
            }} 
          />
        )}
      </div>

      {/* Stats Cards - Mobile Responsive Grid */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, 1fr)', 
        gap: isMobile ? 12 : 20, 
        marginBottom: 20 
      }}>
        <StatCard 
          title="Period Orders" 
          value={analytics.periodOrders} 
          change={analytics.ordersGrowth} 
          icon={<ShoppingBag size={isMobile ? 18 : 20}/>} 
          color="#2874f0" 
          bg="#eff6ff"
          isMobile={isMobile}
        />
        <StatCard 
          title="Period Revenue" 
          value={formatCurrency(analytics.periodRevenue)} 
          change={analytics.revenueGrowth} 
          icon={<DollarSign size={isMobile ? 18 : 20}/>} 
          color="#10b981" 
          bg="#ecfdf5"
          isMobile={isMobile}
        />
        <StatCard 
          title="Pending Deliveries" 
          value={analytics.pending} 
          icon={<Truck size={isMobile ? 18 : 20}/>} 
          color="#f59e0b" 
          bg="#fffbeb"
          isMobile={isMobile}
        />
        <StatCard 
          title="Total Revenue" 
          value={formatCurrency(analytics.totalRevenue)} 
          icon={<Package size={isMobile ? 18 : 20}/>} 
          color="#8b5cf6" 
          bg="#f5f3ff"
          isMobile={isMobile}
        />
      </div>

      {/* Chart and Status - Stack on Mobile */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', 
        gap: isMobile ? 16 : 20, 
        marginBottom: 20 
      }}>
        {/* Revenue Trend Chart - Mobile Optimized */}
        <div style={{ background: 'white', borderRadius: 12, padding: isMobile ? 16 : 20, border: '1px solid #e2e8f0' }}>
          <h3 style={{ fontSize: isMobile ? '0.85rem' : '0.9rem', fontWeight: 600, marginBottom: 16 }}>
            Revenue Trend (Last 7 Days)
          </h3>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: isMobile ? 4 : 12, height: isMobile ? 140 : 180, marginBottom: 12 }}>
            {analytics.dailyData.map((d, i) => (
              <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                <div style={{ 
                  width: '100%', 
                  height: `${(d.revenue / maxRevenue) * (isMobile ? 100 : 140)}px`, 
                  background: '#2874f0', 
                  borderRadius: 4, 
                  opacity: 0.8,
                  minHeight: d.revenue > 0 ? 4 : 0
                }} />
                <span style={{ fontSize: isMobile ? '0.55rem' : '0.65rem', color: '#64748b', transform: isMobile ? 'rotate(-45deg)' : 'none', whiteSpace: 'nowrap' }}>
                  {isMobile ? d.date.slice(0, 3) : d.date}
                </span>
              </div>
            ))}
          </div>
          <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: 12, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
            <div>
              <div style={{ fontSize: isMobile ? '0.65rem' : '0.7rem', color: '#64748b' }}>Total Orders</div>
              <div style={{ fontWeight: 600, fontSize: isMobile ? '0.9rem' : '1rem' }}>{analytics.totalOrdersCount}</div>
            </div>
            <div>
              <div style={{ fontSize: isMobile ? '0.65rem' : '0.7rem', color: '#64748b' }}>Completion Rate</div>
              <div style={{ fontWeight: 600, color: '#10b981', fontSize: isMobile ? '0.9rem' : '1rem' }}>
                {((analytics.statusCounts.delivered / analytics.totalOrdersCount) * 100).toFixed(1)}%
              </div>
            </div>
            <div>
              <div style={{ fontSize: isMobile ? '0.65rem' : '0.7rem', color: '#64748b' }}>Pending</div>
              <div style={{ fontWeight: 600, color: '#f59e0b', fontSize: isMobile ? '0.9rem' : '1rem' }}>
                {analytics.statusCounts.pending + analytics.statusCounts.confirmed}
              </div>
            </div>
          </div>
        </div>

        {/* Order Status Distribution - Mobile Optimized */}
        <div style={{ background: 'white', borderRadius: 12, padding: isMobile ? 16 : 20, border: '1px solid #e2e8f0' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={{ fontSize: isMobile ? '0.85rem' : '0.9rem', fontWeight: 600 }}>Order Status</h3>
            {isMobile && Object.keys(analytics.statusCounts).length > 3 && (
              <button 
                onClick={() => setShowAllStatus(!showAllStatus)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#2874f0',
                  fontSize: '0.7rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4
                }}
              >
                {showAllStatus ? 'Show Less' : `+${Object.keys(analytics.statusCounts).length - 3} more`}
              </button>
            )}
          </div>
          
          {(showAllStatus ? Object.entries(analytics.statusCounts) : getTopStatuses()).map(([status, count]) => {
            const percent = analytics.totalOrdersCount ? (count / analytics.totalOrdersCount) * 100 : 0;
            return (
              <div key={status} style={{ marginBottom: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontSize: isMobile ? '0.75rem' : '0.8rem' }}>
                  <span style={{ textTransform: 'capitalize' }}>{statusLabels[status] || status}</span>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <span style={{ fontWeight: 600, color: statusColors[status] }}>{count}</span>
                    <span style={{ color: '#64748b' }}>({percent.toFixed(1)}%)</span>
                  </div>
                </div>
                <div style={{ width: '100%', height: 6, background: '#e2e8f0', borderRadius: 3, overflow: 'hidden' }}>
                  <div style={{ 
                    width: `${percent}%`, 
                    height: '100%', 
                    background: statusColors[status], 
                    borderRadius: 3,
                    transition: 'width 0.3s ease'
                  }} />
                </div>
              </div>
            );
          })}
          
          {/* Quick Stats Summary for Mobile */}
          {isMobile && (
            <div style={{ 
              marginTop: 16, 
              paddingTop: 12, 
              borderTop: '1px solid #e2e8f0',
              display: 'flex',
              justifyContent: 'space-around',
              gap: 8
            }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '0.6rem', color: '#64748b' }}>Completed</div>
                <div style={{ fontWeight: 700, color: '#10b981' }}>{analytics.statusCounts.delivered}</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '0.6rem', color: '#64748b' }}>In Progress</div>
                <div style={{ fontWeight: 700, color: '#2874f0' }}>
                  {analytics.statusCounts.confirmed + analytics.statusCounts.processing + analytics.statusCounts.out}
                </div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '0.6rem', color: '#64748b' }}>Cancelled</div>
                <div style={{ fontWeight: 700, color: '#ef4444' }}>{analytics.statusCounts.cancelled}</div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Recent Orders Table - Mobile Optimized with Card View */}
      <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
        <div style={{ padding: isMobile ? '12px 16px' : '16px 20px', borderBottom: '1px solid #e2e8f0' }}>
          <h3 style={{ fontSize: isMobile ? '0.85rem' : '0.9rem', fontWeight: 600 }}>Recent Orders</h3>
        </div>
        
        {isMobile ? (
          // Mobile Card View for Recent Orders
          <div style={{ padding: '12px' }}>
            {orders?.slice(0, 10).map(order => (
              <div 
                key={order.id} 
                style={{ 
                  background: '#f8fafc', 
                  borderRadius: 10, 
                  padding: '12px', 
                  marginBottom: 8,
                  border: '1px solid #e2e8f0'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                  <div>
                    <span style={{ fontWeight: 600, color: '#2874f0', fontSize: '0.8rem' }}>
                      #{order.id?.slice(0, 8)}
                    </span>
                    <div style={{ fontSize: '0.7rem', color: '#64748b', marginTop: 2 }}>
                      {order.customerName}
                    </div>
                  </div>
                  <div style={{ fontWeight: 700, fontSize: '0.85rem', color: '#0f172a' }}>
                    ₹{order.totalAmount?.toLocaleString()}
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ 
                    padding: '2px 8px', 
                    borderRadius: 12, 
                    fontSize: '0.6rem', 
                    background: order.orderStatus === 'delivered' ? '#ecfdf5' : order.orderStatus === 'cancelled' ? '#fef2f2' : '#eff6ff', 
                    color: order.orderStatus === 'delivered' ? '#10b981' : order.orderStatus === 'cancelled' ? '#ef4444' : '#2874f0' 
                  }}>
                    {order.orderStatus}
                  </span>
                  <span style={{ fontSize: '0.65rem', color: '#64748b' }}>
                    {new Date(order.orderDate).toLocaleDateString()}
                  </span>
                </div>
              </div>
            ))}
            {orders?.length === 0 && (
              <div style={{ textAlign: 'center', padding: 40, color: '#64748b' }}>
                <Package size={40} color="#cbd5e1" />
                <p style={{ marginTop: 12, fontSize: '0.8rem' }}>No orders found</p>
              </div>
            )}
          </div>
        ) : (
          // Desktop Table View
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 600 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #e2e8f0', background: '#f8fafc' }}>
                  <th style={{ textAlign: 'left', padding: '12px 16px', fontSize: '0.7rem', fontWeight: 600, color: '#64748b' }}>Order ID</th>
                  <th style={{ textAlign: 'left', padding: '12px 16px', fontSize: '0.7rem', fontWeight: 600, color: '#64748b' }}>Customer</th>
                  <th style={{ textAlign: 'left', padding: '12px 16px', fontSize: '0.7rem', fontWeight: 600, color: '#64748b' }}>Amount</th>
                  <th style={{ textAlign: 'left', padding: '12px 16px', fontSize: '0.7rem', fontWeight: 600, color: '#64748b' }}>Status</th>
                  <th style={{ textAlign: 'left', padding: '12px 16px', fontSize: '0.7rem', fontWeight: 600, color: '#64748b' }}>Date</th>
                </tr>
              </thead>
              <tbody>
                {orders?.slice(0, 10).map(order => (
                  <tr key={order.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '12px 16px', fontSize: '0.8rem' }}>
                      <span style={{ fontWeight: 600, color: '#2874f0' }}>#{order.id?.slice(0, 8)}</span>
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: '0.8rem' }}>{order.customerName}</td>
                    <td style={{ padding: '12px 16px', fontSize: '0.8rem' }}>₹{order.totalAmount?.toLocaleString()}</td>
                    <td style={{ padding: '12px 16px', fontSize: '0.8rem' }}>
                      <span style={{ 
                        padding: '4px 10px', 
                        borderRadius: 12, 
                        fontSize: '0.7rem', 
                        background: order.orderStatus === 'delivered' ? '#ecfdf5' : order.orderStatus === 'cancelled' ? '#fef2f2' : '#eff6ff', 
                        color: order.orderStatus === 'delivered' ? '#10b981' : order.orderStatus === 'cancelled' ? '#ef4444' : '#2874f0' 
                      }}>
                        {order.orderStatus}
                      </span>
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: '0.8rem', color: '#64748b' }}>
                      {new Date(order.orderDate).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {orders?.length === 0 && (
              <div style={{ textAlign: 'center', padding: 60, color: '#64748b' }}>
                <Package size={48} color="#cbd5e1" />
                <p style={{ marginTop: 16 }}>No orders found</p>
              </div>
            )}
          </div>
        )}
      </div>
      
      <style>{`
        .spinner { 
          animation: spin 0.6s linear infinite; 
        }
        @keyframes spin { 
          to { transform: rotate(360deg); } 
        }
      `}</style>
    </div>
  );
};

const StatCard = ({ title, value, change, icon, color, bg, isMobile }) => (
  <div style={{ 
    background: 'white', 
    borderRadius: 12, 
    padding: isMobile ? 14 : 20, 
    border: '1px solid #e2e8f0',
    transition: 'transform 0.2s, box-shadow 0.2s',
    cursor: 'pointer'
  }}
  onMouseEnter={(e) => {
    e.currentTarget.style.transform = 'translateY(-2px)';
    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)';
  }}
  onMouseLeave={(e) => {
    e.currentTarget.style.transform = 'translateY(0)';
    e.currentTarget.style.boxShadow = 'none';
  }}
  >
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: isMobile ? 10 : 12 }}>
      <div style={{ 
        width: isMobile ? 36 : 40, 
        height: isMobile ? 36 : 40, 
        borderRadius: 10, 
        background: bg, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        color 
      }}>
        {icon}
      </div>
      {change !== undefined && (
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 3, 
          padding: '2px 8px', 
          borderRadius: 16, 
          background: change >= 0 ? '#f0fdf4' : '#fef2f2', 
          fontSize: isMobile ? '0.65rem' : '0.7rem', 
          color: change >= 0 ? '#16a34a' : '#dc2626',
          fontWeight: 500
        }}>
          {change >= 0 ? <TrendingUp size={isMobile ? 10 : 12}/> : <TrendingDown size={isMobile ? 10 : 12}/>}
          {Math.abs(change).toFixed(1)}%
        </div>
      )}
    </div>
    <div style={{ 
      fontSize: isMobile ? '1.2rem' : '1.5rem', 
      fontWeight: 700, 
      color: '#0f172a', 
      marginBottom: 4,
      lineHeight: 1.2
    }}>
      {value}
    </div>
    <div style={{ fontSize: isMobile ? '0.7rem' : '0.8rem', color: '#64748b' }}>{title}</div>
  </div>
);

export default Dashboard;